import { faAsterisk, faBasketShopping, faCalendar, faPeopleGroup, faPerson, faPersonDigging, faSackDollar, faSeedling, faTimeline, faWeightHanging } from "@fortawesome/free-solid-svg-icons";
import HTMLCode from "components/common/HTMLCode";
import If from "components/common/If";
import ItemView, { ItemViewPropsType } from "components/common/views/ItemView";
import PropertyViewValue from "components/common/views/PropertyViewValue";
import { useTranslation } from "react-i18next";
import { HarvestsDatalistQuery, WorkViewPageQuery } from "types/graphql";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import * as lodash from "lodash";
import ListOfNamesView from "components/common/views/ListOfNamesView";
import BooleanView from "components/common/views/BooleanView";

type ListItemType = Exclude<HarvestsDatalistQuery['harvestAgricultureWorks'], null | undefined>['items'][0];
type ViewType = Exclude<WorkViewPageQuery['harvestAgricultureWork'], null | undefined>;

type WorkViewPropsType = {
    model: ViewType | ListItemType,
    view: 'compact' | 'default',
    name: string
}

const WorkView = (props: WorkViewPropsType) => {
    const [t] = useTranslation();

    const harvest = 'kgSum' in props.model.harvests ? [{
        items: [
            {
                icon: faWeightHanging,
                label: 'fields.kg.shortName',
                name: 'kg',
                value: (
                    <>
                        {formatMoney(props.model.harvests!.kgSum)} kg
                    </>
                )
            },
            {
                icon: faBasketShopping,
                label: 'fields.cart.shortName',
                name: 'cart',
                value: props.model.harvests?.cartQuantitySum,
            },
        ],
        name: "harvest-data",
    } as ItemViewPropsType['model'][0]] : (props.model.harvests?.items?.map((harvest, i) => {
            const h = harvest as Exclude<Exclude<Exclude<ViewType['harvests'], null | undefined>['items'], null | undefined>[0], null | undefined>;
            return {
                items: [
                    {
                        icon: faSeedling,
                        label: 'fields.fruit.shortName',
                        name: `fruit-${i}`,
                        value: h?.fruit?.name
                    },
                    {
                        icon: faWeightHanging,
                        label: 'fields.kg.shortName',
                        name: `kg-${i}`,
                        value: (
                            <>
                                {formatMoney(h!.kg)} kg
                            </>
                        )
                    },
                    {
                        icon: faBasketShopping,
                        label: 'fields.cart.shortName',
                        name: `cart-${i}`,
                        value: (
                            <>
                                {h?.cartQuantity}
                                <If condition={props.view === 'default' && h}>
                                    <span> / {(Number(h!.kg) / h!.cartQuantity).toFixed(2)} {t('common.perCart')}</span>
                                </If>
                            </>
                        ),
                    },
                    {
                        label: 'fields.group.shortName',
                        name: `fruitGroup-${i}`,
                        hide: 'compact',
                        value: <PropertyViewValue value={h?.harvestFruitGroups?.items?.map(x => x!.fruitGroup.name)} />
                    },
                    {
                        label: 'fields.category.shortName',
                        name: `fruitCategory-${i}`,
                        hide: 'compact',
                        value: <PropertyViewValue value={h?.harvestFruitCategories?.items?.map(x => x!.fruitCategory.name)} />
                    },
                    {
                        label: 'fields.hide-from-multi-update.name',
                        name: `hide-from-multi-update-${i}`,
                        hide: 'compact',
                        value: <BooleanView value={h?.hideFromMultiUpdate} />
                    }
                ],
                name: `harvest-${i}`,
                className: {
                    default: 'border-b theme-border',
                }
            } as ItemViewPropsType['model'][0];
        }) || []).flat();

    const properties: ItemViewPropsType['model'] = [
        {
            name: 'id',
            hide: 'compact',
            items: [{
                icon: faAsterisk,
                label: 'fields.name.shortName',
                value: 'name' in props.model ? props.model.name : '',
                hide: 'compact',
                name: "name"
            },{
                icon: faAsterisk,
                label: 'fields.id.shortName',
                value: props.model.id,
                hide: 'compact',
                name: "id"
            }]
        },
        {
            name: 'date-fruit',
            items: [
                {
                    icon: faCalendar,
                    label: 'fields.startDate.shortName',
                    name: 'startDate',
                    value: DateUtil.format(new Date(props.model.startDate), DateFormats.SHORT)
                },
                {
                    icon: faPersonDigging,
                    label: 'fields.type.shortName',
                    name: 'type',
                    value: props.model.harvests ? t('agriculture.work.type.harvest') : ''
                }
            ]
        },
        ...harvest,
        {
            name: 'workers-data',
            items: [
                {
                    icon: faPeopleGroup,
                    label: 'fields.workersCount.name',
                    name: 'workersCount',
                    value: props.model.workers?.count
                },
                {
                    icon: faSackDollar,
                    label: 'fields.workersSum.name',
                    name: 'workersSum',
                    value: <>{formatMoney(props.model.workers?.priceSum)} <HTMLCode code="euro" /></>
                },
                {
                    icon: faPeopleGroup,
                    label: 'fields.workers.shortName',
                    name: 'workers',
                    hide: 'compact',
                    value: `${(('kgSum' in props.model.harvests ? props.model.harvests.kgSum : lodash.sum(props.model.harvests.items.map(x => 'kg' in x ? x.kg : 0))) / props.model.workers?.count).toFixed(2)} KG / 👷🏻`
                },
                ...('items' in props.model.workers! ? props.model.workers?.items?.map((w, i) => {
                    return {
                        icon: faPerson,
                        label: '',
                        name: 'person-' + i,
                        hide: 'compact',
                        value: <>{i + 1}. {w?.person?.name}, <i>{formatMoney(w?.price)} <HTMLCode code="euro" /></i></>
                    }
                }) || [] : []) as any,
            ],
            className: {
                compact: 'grid text-center grid-cols-2'
            }
        },
        {
            name: 'other-info',
            items: [
                {
                    icon: faTimeline,
                    label: 'fields.season.shortName',
                    name: 'season',
                    value: props.model?.season?.name
                },
                {
                    icon: faSeedling,
                    label: 'fields.fruit.shortName',
                    name: 'fruit',
                    hide: 'default',
                    value: <ListOfNamesView data={props.model.harvests?.items?.map(h => h?.fruit?.name)} distinct />
                },
            ]
        },
        {
            items: [
                {
                    label: 'fields.note.name',
                    name: 'note',
                    value: 'note' in props.model ? props.model.note : '',
                    hide: "compact",
                    propertyType: 'note'
                }
            ],
            hide: "compact",
            name: 'note'
        }
    ]

    return <ItemView name={props.name} model={properties} view={props.view} />
};

export default WorkView;
